import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { getQuestionsData } from "../utils/app.utils";
import Text from "./fields/Text";
import RadioButton from "./fields/RadioButton";
import RatingField from "./fields/RatingField";
import SingleChoice from "./fields/SingleChoice";
import MultipleChoice from "./fields/MultipleChoice";
import AudioField from "./fields/AudioField";
import NumberRating from "./fields/NumberRating";
import AudioRecorder from "./fields/AudioRecorder";
import DateField from "./fields/DateField";

// Map question types to their answer components
const fieldComponentMap = {
  text: Text,
  radiobutton: RadioButton,
  rating: RatingField,
  single: SingleChoice,
  multiple: MultipleChoice,
  audio: AudioField,
  numberrating: NumberRating,
  speech: AudioRecorder,
  date: DateField,
};

type FieldType =
  | "text"
  | "radiobutton"
  | "rating"
  | "single"
  | "multiple"
  | "audio"
  | "numberrating"
  | "speech"
  | "date";

interface Question {
  id: number;
  type: FieldType | string;
  name: string;
}

// Cast or declare question's type

function htmlToText(html: string) {
  const div = document.createElement("div");
  div.innerHTML = html || "";
  return div.textContent || div.innerText || "";
}

export const SectionContent: React.FC<{
  section: any;
  answers: Record<number, any>;
  onAnswerChange: (questionId: number, value: any) => void;
}> = ({ section, answers, onAnswerChange }) => {
  // Store applicant answers: { [question.id]: value }

  const { data: questionsData = [], isLoading } = useQuery({
    queryKey: ["sectionQuestion", section?.id],
    queryFn: () => getQuestionsData(section?.id),
  });

  if (!section) return null;

  // Section description or media
  let sectionDescription: React.ReactNode = null;
  if (section.text_to_speech_flag) {
    sectionDescription = <AudioField question={section} isSection />;
  } else if (section.description) {
    sectionDescription = (
      <Typography
        variant="subtitle1"
        sx={{ mb: 2 }}
        component="div"
        dangerouslySetInnerHTML={{ __html: section.description }}
      />
    );
  }

  const UnsupportedField: React.FC<{
    question: Question;
    value?: any;
    onChange?: (val: any) => void;
  }> = ({ question }) => (
    <Typography color="error">
      Unsupported field type: {question.type}
    </Typography>
  );

  return (
    <div>
      {sectionDescription}
      {isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{ mt: 2 }}
        >
          <CircularProgress />
        </Box>
      ) : questionsData.length === 0 ? (
        <Typography>No questions available.</Typography>
      ) : (
        questionsData.map((question: any) => {
          const typedQuestionType = question.type as FieldType;

          // Question text or media
          const questionText = question.text_to_speech_flag ? (
            <AudioField question={question} />
          ) : (
            <Typography variant="subtitle1" sx={{ mt: 3 }}>
              {question.question_no} . {question.name}
            </Typography>
          );

          // Dynamic answer field
          const AnswerComponent =
            fieldComponentMap[typedQuestionType] ?? UnsupportedField;

          return (
            <div key={question.id} style={{ marginBottom: "2rem" }}>
              {questionText}
              <AnswerComponent
                question={question}
                value={answers[question.id] ?? ""}
                onChange={(val) => onAnswerChange(question.id, val)}
              />
            </div>
          );
        })
      )}
    </div>
  );
};
